import { useState } from 'react'
import { useWakeLock } from '../platform/device'
import { finalizeDraft, saveDraft, type StopDraft } from '../sync/drafts'
import { actualSequenceFor, type DriverStop } from '../sync/route'
import { CONDITIONS, ITEM_TITLES, SKIP_REASONS, bigButton, button, card, color, container, footer, h1, muted, page, textButton } from '../ui'

/**
 * STOP COMPLETE (v3 section 9): one large button, no confirmation dialog.
 * A free-text note is a plain textarea - Android's keyboard dictation is the
 * voice input. Also the SKIP screen, same shape: tap a reason, one button.
 */
export function CompleteScreen({
  stop,
  draft,
  mode,
  onDone,
  onBack,
}: {
  stop: DriverStop
  draft: StopDraft
  mode: 'complete' | 'skip'
  onDone: () => void
  onBack: () => void
}) {
  const [note, setNote] = useState(draft.note)
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  useWakeLock(true)

  const items = stop.checklist_required.map((t) => ({ type: t, ...draft.items[t] }))
  const photos = items.filter((i) => i?.hasPhoto).length
  const skipped = items.filter((i) => i?.skipped || (!i?.hasPhoto && !i?.condition)).length

  const finish = async () => {
    setBusy(true)
    try {
      const d = { ...draft, note }
      await saveDraft(d)
      const seq = await actualSequenceFor(stop.uuid, draft.routeId)
      await finalizeDraft(d, mode === 'skip' ? 'skipped' : 'complete', seq, mode === 'skip' ? reason || 'Other' : '')
      onDone()
    } finally {
      setBusy(false)
    }
  }

  return (
    <div style={page}>
      <div style={container}>
        <button style={{ ...textButton, paddingLeft: 0 }} onClick={onBack}>‹ Back</button>
        <div style={muted}>{stop.name}</div>
        <div style={h1}>{mode === 'skip' ? 'Skip this stop' : 'Finish this stop'}</div>

        {mode === 'skip' ? (
          <div style={{ display: 'grid', gap: 10, marginBottom: 12 }}>
            {SKIP_REASONS.map((r) => (
              <button key={r} style={{ ...button(reason === r ? color.amber : color.white, reason === r ? color.white : color.ink), minHeight: 64, fontSize: 19, textAlign: 'left' }} onClick={() => setReason(r)}>
                {r}
              </button>
            ))}
          </div>
        ) : (
          <div style={card}>
            <div style={{ fontSize: 19, marginBottom: 6 }}>
              <strong>{photos}</strong> photo{photos === 1 ? '' : 's'} · <strong>{skipped}</strong> item{skipped === 1 ? '' : 's'} skipped
            </div>
            {items.map((i) => (
              <div key={i.type} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 17, padding: '4px 0', borderTop: `1px solid ${color.greyLight}` }}>
                <span>{ITEM_TITLES[i.type] ?? i.type}</span>
                <span style={{ color: i?.hasPhoto || i?.condition ? color.green : color.grey, fontWeight: 700 }}>
                  {i?.hasPhoto ? '📷 ' : ''}
                  {i?.condition ? CONDITIONS[i.type]?.find((c) => c.value === i.condition)?.label ?? i.condition : i?.hasPhoto ? '' : 'skipped'}
                </span>
              </div>
            ))}
          </div>
        )}

        <div style={{ ...muted, marginBottom: 6 }}>{mode === 'skip' ? 'Anything else (optional)' : 'Note for the office (optional)'}</div>
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={4}
          placeholder={mode === 'skip' ? 'What happened' : 'Anything worth knowing'}
          style={{ width: '100%', fontSize: 19, padding: 12, borderRadius: 12, border: `2px solid ${color.greyLight}`, boxSizing: 'border-box', marginBottom: 96 }}
        />
      </div>

      <div style={footer}>
        <button style={bigButton(mode === 'skip' ? color.amber : color.green, busy)} disabled={busy} onClick={finish}>
          {busy ? 'Saving…' : mode === 'skip' ? 'Skip stop' : 'Complete stop ✓'}
        </button>
      </div>
    </div>
  )
}
